/**
 * API Client for ERP SPA.
 *
 * Uses /api/v3/ prefix when served via Nginx.
 * Falls back to localhost:3000 during dev (via Vite proxy).
 *
 * Refresh flow:
 *  - Access token is stored in memory (not localStorage).
 *  - On 401, the silent-refresh interceptor calls POST /auth/refresh.
 *  - If refresh succeeds, the original request is retried with the new token.
 *  - If refresh fails, the user is redirected to /login.
 */

const API_BASE = import.meta.env.VITE_API_BASE || '';

interface ApiError {
  status: number;
  detail: string;
  retryAfter?: number;
}

// ---------------------------------------------------------------------------
// In-memory token storage (safer than localStorage for XSS protection)
// ---------------------------------------------------------------------------
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ---------------------------------------------------------------------------
// Silent refresh state — prevents concurrent refresh storms
// ---------------------------------------------------------------------------
let refreshPromise: Promise<boolean> | null = null;

async function silentRefresh(): Promise<boolean> {
  // If a refresh is already in-flight, wait on it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v3/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // sends httpOnly cookie
      });

      if (!res.ok) {
        accessToken = null;
        return false;
      }

      const data = await res.json();
      accessToken = data.access_token;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Login, refresh y logout: su 401 es la respuesta (credenciales o sesión inválidas) y no se renueva ni se reintenta.
 * Cualquier otro endpoint, también los de auth como auth/me/preferencias, renueva la sesión vencida y reintenta.
 */
const esEndpointDeSesion = (endpoint: string) => /\/auth\/(login|refresh|logout)(\?|$)/.test(endpoint);

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------
async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  allowRetry = true,
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(url, {
    headers,
    credentials: 'include',
    ...options,
  });

  const data = await res.json().catch(() => null);

  // Handle 429 — rate limited
  if (res.status === 429) {
    const err: ApiError = {
      status: 429,
      detail: data?.message ?? 'Demasiados intentos. Esperá unos segundos.',
      // Segundos de la cabecera Retry-After (la pone el throttler del backend); sin cabecera, quien llama decide.
      retryAfter: Number(res.headers?.get('Retry-After')) || data?.retryAfter,
    };
    throw err;
  }

  // Silent refresh on 401 (only if not already refreshing and not one of the session endpoints themselves)
  if (res.status === 401 && allowRetry && !esEndpointDeSesion(endpoint)) {
    const refreshed = await silentRefresh();

    if (refreshed) {
      // Retry the original request with the new token
      const retryHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };
      retryHeaders['Authorization'] = `Bearer ${accessToken}`;

      const retryRes = await fetch(url, {
        headers: retryHeaders,
        credentials: 'include',
        ...options,
      });

      const retryData = await retryRes.json().catch(() => null);

      if (!retryRes.ok) {
        // Refresh succeeded but retry still failed — bubble the error
        const err: ApiError = {
          status: retryRes.status,
          detail: retryData?.detail ?? retryData?.message ?? `HTTP ${retryRes.status}`,
        };
        throw err;
      }

      return retryData as T;
    }

    // Refresh failed — redirect to login
    accessToken = null;
    window.location.href = '/erp/login';
    // Never resolve — the redirect happens
    return new Promise(() => {});
  }

  if (!res.ok) {
    const err: ApiError = {
      status: res.status,
      detail: data?.detail ?? data?.message ?? `HTTP ${res.status}`,
    };
    throw err;
  }

  return data as T;
}

// ---------------------------------------------------------------------------
// Public API surface
// ---------------------------------------------------------------------------
export const api = {
  get: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'GET' }),

  getWithResponse: async <T>(endpoint: string) => {
    const url = `${API_BASE}${endpoint}`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const res = await fetch(url, { headers, credentials: 'include' });
    const data = (await res.json()) as T;

    if (res.status === 401) {
      const refreshed = await silentRefresh();
      if (refreshed) {
        // Retry
        const retryHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        retryHeaders['Authorization'] = `Bearer ${accessToken}`;
        const retryRes = await fetch(url, { headers: retryHeaders, credentials: 'include' });
        const retryData = await retryRes.json();
        if (!retryRes.ok) throw { status: retryRes.status, detail: retryData?.detail ?? `HTTP ${retryRes.status}` } as ApiError;
        return { data: retryData as T, headers: retryRes.headers };
      }
      accessToken = null;
      window.location.href = '/erp/login';
      return { data: null as unknown as T, headers: new Headers() } as { data: T; headers: Headers };
    }

    if (!res.ok) {
      throw {
        status: res.status,
        detail: (data as any)?.detail ?? `HTTP ${res.status}`,
      } as ApiError;
    }
    return { data, headers: res.headers };
  },

  post: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  put: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),

  upload: async <T>(endpoint: string, file: File, extraFields?: Record<string, string>): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file);
    if (extraFields) {
      for (const [k, v] of Object.entries(extraFields)) formData.append(k, v);
    }
    const url = `${API_BASE}${endpoint}`;
    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    });

    if (res.status === 401) {
      const refreshed = await silentRefresh();
      if (refreshed) {
        const retryHeaders: Record<string, string> = {};
        retryHeaders['Authorization'] = `Bearer ${accessToken}`;
        const retryRes = await fetch(url, {
          method: 'POST',
          body: formData,
          headers: retryHeaders,
          credentials: 'include',
        });
        if (!retryRes.ok) {
          const retryData = await retryRes.json().catch(() => null);
          throw {
            status: retryRes.status,
            detail: retryData?.detail || `HTTP ${retryRes.status}`,
          };
        }
        return retryRes.json();
      }
      accessToken = null;
      window.location.href = '/erp/login';
      return new Promise(() => {});
    }

    if (!res.ok) {
      throw {
        status: res.status,
        detail: (await res.json().catch(() => null))?.detail || `HTTP ${res.status}`,
      };
    }
    return res.json();
  },
};

export function extractErrorMessage(err: unknown): string {
  if (!err) return 'Error desconocido';
  if (typeof err === 'object' && 'detail' in err) {
    return typeof (err as any).detail === 'string'
      ? (err as any).detail
      : JSON.stringify((err as any).detail);
  }
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return JSON.stringify(err);
}
